import { useEffect, useState } from "react";
import { getAbsoluteJson, getJson } from "../api";
import CameraCapture from "../components/CameraCapture";
import {
  buildRows,
  CaptureResultCard,
  FeedbackPanel,
  formatDate,
  InfoCard,
  PortalCard,
  PortalLayout,
  StatusBadge,
} from "./shared";

export function PortalHomeScreen() {
  return (
    <main className="app-shell">
      <section className="hero">
        <p className="eyebrow">Camionero Digital</p>
        <h1>Choferes, empresas de transporte y unidades</h1>
        <p className="hero-copy">
          La portada principal queda enfocada en identidad de choferes, operacion de empresas de transporte y gestion documental de unidades.
        </p>
      </section>

      <section className="portal-grid">
        <PortalCard
          eyebrow="Portal 01"
          title="Choferes"
          description="Ingreso, recuperación de cuenta y alta autónoma del chofer con validación biométrica."
          items={["Login con el método elegido", "Recuperación con DNI + selfie", "Alta del chofer", "Permisos de acceso para empresas"]}
          href="#/portal-chofer"
        />
        <PortalCard
          eyebrow="Portal 02"
          title="Empresas de transporte"
          description="Autenticacion tradicional separada del chofer, con su app operativa para flota y eventos."
          items={["Auth por CUIT y password", "Unidades y acoplados", "Solicitud de acceso a choferes", "Detalle operativo por evento"]}
          href="#/portal-empresa-transporte/auth"
        />
        <PortalCard
          eyebrow="Portal 03"
          title="Unidades"
          description="Pantalla central para ver unidades ya validadas y dar de alta tractores o acoplados por foto o por archivo."
          items={["Listado de unidades", "Filtro por dominio", "Alta asistida por foto", "Alta asistida por archivo"]}
          href="#/portal-empresa-transporte/app/flota"
        />
      </section>
    </main>
  );
}

export function CameraDemoScreen() {
  const [selfieFile, setSelfieFile] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);

  return (
    <PortalLayout
      eyebrow="Demo de captura"
      title="Camara reusable mobile-first"
      description="Prueba aislada del componente de captura. No sube nada ni conecta Rekognition todavia."
      backHref="#/"
    >
      <section className="camera-demo-grid">
        <CameraCapture mode="selfie" title="Captura selfie" description="Prioriza camara frontal. Si no existe, usa la mejor camara disponible." onConfirm={setSelfieFile} />
        <CameraCapture mode="documento" title="Captura documento" description="Prioriza camara trasera para fotografiar DNI o documentacion." onConfirm={setDocumentFile} />
      </section>

      <section className="detail-panel detail-panel-wide">
        <div className="detail-panel-header">
          <h2>Resultado de la captura</h2>
        </div>
        <div className="capture-result-grid">
          <CaptureResultCard label="Selfie" file={selfieFile} />
          <CaptureResultCard label="Documento" file={documentFile} />
        </div>
      </section>
    </PortalLayout>
  );
}

export function HealthScreen() {
  const [health, setHealth] = useState(null);
  const [dependencies, setDependencies] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [healthResponse, dependenciesResponse] = await Promise.all([
          getAbsoluteJson("/health"),
          getAbsoluteJson("/health/dependencies"),
        ]);
        if (!active) {
          return;
        }
        setHealth(healthResponse);
        setDependencies(dependenciesResponse);
      } catch (requestError) {
        if (active) {
          setError(requestError.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  return (
    <PortalLayout eyebrow="Observabilidad" title="Health de staging" description="Chequeo rapido del backend, la base y dependencias configuradas para el ambiente actual." backHref="#/">
      {loading ? <p className="status-message">Consultando health...</p> : null}
      {error ? <p className="status-message">{error}</p> : null}

      {!loading && !error ? (
        <section className="portal-section-grid">
          <InfoCard title="Health" rows={buildRows(health, ["status", "app_env", "db", "database_time"])} />
          <InfoCard
            title="Dependencias"
            rows={buildRows(
              {
                db: dependencies?.dependencies?.db?.ok ? "ok" : "error",
                s3: dependencies?.dependencies?.s3?.ok ? "ok" : "error",
                s3_mode: dependencies?.dependencies?.s3?.mode || "-",
                rekognition: dependencies?.dependencies?.rekognition?.configured ? "configurado" : "faltante",
                rto_mode: dependencies?.dependencies?.rto?.mode || "-",
                rto_configured: dependencies?.dependencies?.rto?.configured ? "si" : "no",
              },
              ["db", "s3", "s3_mode", "rekognition", "rto_mode", "rto_configured"]
            )}
          />
          <InfoCard title="Signed URL probe" rows={buildRows(dependencies?.dependencies?.s3?.signed_url_probe || {})} />
        </section>
      ) : null}
    </PortalLayout>
  );
}

export function EventDetailScreen({ eventId, backHash, portalTitle }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openingDocumentId, setOpeningDocumentId] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const response = await getJson(`/eventos/${eventId}/detalle`);
        if (!cancelled) {
          setData(response.data);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message);
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const openDocument = async (documentId) => {
    setOpeningDocumentId(String(documentId));
    try {
      const response = await getJson(`/documentos/${documentId}/url`);
      window.open(response.data.url, "_blank", "noopener,noreferrer");
    } catch (openError) {
      setError(openError.message);
    } finally {
      setOpeningDocumentId("");
    }
  };

  return (
    <PortalLayout eyebrow={portalTitle} title={`Detalle de evento #${eventId}`} description="Vista consolidada de evento, entidades relacionadas, biometria y verificacion documental." backHref={backHash}>
      <section className="detail-hero">
        <StatusBadge label={data?.apto_para_operar ? "Apto para operar" : "No apto para operar"} tone={data?.apto_para_operar ? "vigente" : "vencido"} large />
      </section>

      {loading ? <FeedbackPanel message="Cargando detalle del evento..." /> : null}
      {error ? <FeedbackPanel message={error} error /> : null}

      {!loading && !error && data ? (
        <section className="detail-grid">
          <InfoCard
            title="Evento"
            rows={buildRows(
              data.evento,
              ["id", "empresa", "chofer_cuit", "unidad_dominio", "acoplado_dominio", "created_at"],
              { created_at: "Fecha del evento" }
            )}
          />
          <InfoCard title="Chofer" rows={buildRows(data.chofer)} />
          <InfoCard title="Unidad" rows={buildRows(data.unidad)} />
          <InfoCard title="Acoplado" rows={buildRows(data.acoplado)} />
          <InfoCard
            title="Biometria"
            rows={buildRows(data.biometria || { estado: "Sin validacion reciente" }, undefined, { created_at: "Fecha de validacion" })}
          />

          <section className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h2>Verificacion documental</h2>
              <StatusBadge label={data.verificacion_documental.estado_general} tone={data.verificacion_documental.estado_general === "aprobado" ? "vigente" : "vencido"} />
            </div>

            <div className="entity-status-grid">
              {data.verificacion_documental.entidades.map((entity) => (
                <article className="entity-status-card" key={`${entity.entidad_tipo}-${entity.entidad_id}`}>
                  <div className="entity-status-header">
                    <div>
                      <p className="entity-eyebrow">{entity.entidad_tipo}</p>
                      <h3>{entity.entidad_id}</h3>
                    </div>
                    <StatusBadge label={entity.estado_general} tone={entity.estado_general === "aprobado" ? "vigente" : "vencido"} />
                  </div>

                  <div className="document-list">
                    {entity.documentos.map((documentStatus) => (
                      <div className="document-row" key={`${entity.entidad_tipo}-${entity.entidad_id}-${documentStatus.tipo_documento}`}>
                        <div className="document-main">
                          <div className="document-title-row">
                            <strong>{documentStatus.etiqueta}</strong>
                            <StatusBadge label={documentStatus.estado} tone={documentStatus.estado} />
                          </div>
                          <p className="document-reason">{documentStatus.motivo}</p>
                          {documentStatus.documento ? (
                            <p className="document-meta">
                              {documentStatus.documento.nombre_archivo}
                              {documentStatus.documento.fecha_vencimiento ? ` - vence ${formatDate(documentStatus.documento.fecha_vencimiento)}` : ""}
                            </p>
                          ) : null}
                        </div>
                        {documentStatus.documento ? (
                          <button className="secondary-button document-button" type="button" onClick={() => openDocument(documentStatus.documento.id)} disabled={openingDocumentId === String(documentStatus.documento.id)}>
                            {openingDocumentId === String(documentStatus.documento.id) ? "Abriendo..." : "Ver archivo"}
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </section>
      ) : null}
    </PortalLayout>
  );
}
